import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ButtonAccent } from "components/Atoms/Button";
import { Panel } from "components/panels";
import RightSideNavigation from "components/RightSideNavigation/RightSideNavigation";
import { OrderableList } from "components/Tables";
import TwoColumnLayout from "layout/TwoColumnLayout";
import React from "react";
import { useCustomQuery } from "utilities/hookutils";
import { routes } from "utilities/routing/generatedRoutes";
import { useLoaderData } from "react-router";
import { NewFreeFieldCategoryModal } from "FreeFieldCategory/NewFreeFieldCategoryModal";

const FreefieldCategoryListApp = () => {
  const [isNewModalOpen, setIsNewModalOpen] = React.useState(false);
  const viewModel = useLoaderData<server.viewmodel.FreeFieldCategoryListFrontModel>();
  const [freeFieldCategories, setFreeFieldCategories] = React.useState<server.dto.FreeFieldCategoryForList[]>([]);

  const queryKey = ["freefieldCategories", viewModel.mainAreaId];
  useCustomQuery({
    queryFn: () => window.CoreApi.FreeFieldCategory.List(viewModel.mainAreaId).then(r => r.data),
    queryKey,
    onSuccess: (response) => {
      if (response.hasErrors) {
        return;
      }
      setFreeFieldCategories(response.data);
    }
  });

  const queryClient = useQueryClient();

  /* Optimistic update
    https://react-query.tanstack.com/guides/optimistic-updates
    - We update our local state with the new order
    - Then we update the order on the server
    - If the server request fails, we roll back the local state to the previous order
    - After the request is done, we refetch the data
  */
  const sortMutation = useMutation({
    mutationFn: (ffCategories: server.dto.FreeFieldCategoryForList[]) => window.CoreApi.FreeFieldCategory.Sort({
      freeFieldCategoryIds: ffCategories.map(f => f.freeFieldCategoryId),
      mainArea: viewModel.mainAreaId
    }),
    onMutate: async (newOrderedFFCategories) => {
      await queryClient.cancelQueries(queryKey);
      const previousOrder = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, newOrderedFFCategories);
      return { previousOrder };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(queryKey, context.previousOrder);
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey);
    }
  })

  const onOrderChanged = (newOrder: server.dto.FreeFieldCategoryForList[]) => {
    setFreeFieldCategories(newOrder);
    sortMutation.mutate(newOrder);
  };

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <Panel
        headerContainerClasses="top-navigation-wrapper"
        title={viewModel.pageTitle}
        headerSize="h1"
        button={<ButtonAccent label={RESX.Buttons.btnNew} onClick={() => setIsNewModalOpen(true)} />}
      >
        <div className="s1_panel">
          <OrderableList
            getKey={f => f.freeFieldCategoryId.toString()}
            items={freeFieldCategories}
            onOrderChanged={onOrderChanged}
            render={f => <div className="grow1">
              <a href={routes.freeFieldCategory_Detail(f.freeFieldCategoryId).absolute}>{f.title}</a>
            </div>}
          />
        </div>
      </Panel>
    </TwoColumnLayout.LeftColumn>
    <RightSideNavigation
      title={desktopNavigation.rightSideNavigation.title}
      menuItems={desktopNavigation.rightSideNavigation.items}
      currentPage={desktopNavigation.currentPage}
    />
    <NewFreeFieldCategoryModal
      isOpen={isNewModalOpen}
      onClose={() => setIsNewModalOpen(false)}
      mainArea={viewModel.mainAreaId}
    />
  </TwoColumnLayout>
}

export default FreefieldCategoryListApp;